'use client';

import React, { useState } from 'react';
import { Search, Plus, AlertTriangle, TrendingUp, TrendingDown, ChevronDown, ChevronUp, Edit2, Phone, User, CreditCard, History, CheckCircle,  } from 'lucide-react';
import Modal from '@/components/ui/Modal';


interface Buyer {
  id: string;
  name: string;
  mobile: string;
  address: string;
  creditLimit: number;
  outstanding: number;
  totalPurchases: number;
  totalPayments: number;
  lastPurchaseDate: string;
  status: 'active' | 'blocked' | 'watch';
}

interface Transaction {
  id: string;
  date: string;
  type: 'purchase' | 'payment';
  description: string;
  amount: number;
  balance: number;
  lotNumber?: string;
  method?: string;
}

const BUYERS_DATA: Buyer[] = [
  {
    id: 'b-001',
    name: 'Senthil Traders',
    mobile: '9845612370',
    address: 'Koyambedu Market, Chennai',
    creditLimit: 50000,
    outstanding: 42500,
    totalPurchases: 285000,
    totalPayments: 242500,
    lastPurchaseDate: '18/06/2026',
    status: 'watch',
  },
  {
    id: 'b-002',
    name: 'Murugan Wholesale',
    mobile: '9712345678',
    address: 'Thiruvallur, Tamil Nadu',
    creditLimit: 100000,
    outstanding: 0,
    totalPurchases: 520000,
    totalPayments: 520000,
    lastPurchaseDate: '18/06/2026',
    status: 'active',
  },
  {
    id: 'b-003',
    name: 'Karthik Enterprises',
    mobile: '9654321098',
    address: 'Ambattur, Chennai',
    creditLimit: 75000,
    outstanding: 68000,
    totalPurchases: 410000,
    totalPayments: 342000,
    lastPurchaseDate: '18/06/2026',
    status: 'watch',
  },
  {
    id: 'b-004',
    name: 'Priya Vegetable Store',
    mobile: '9876012345',
    address: 'Poonamallee, Chennai',
    creditLimit: 40000,
    outstanding: 32500,
    totalPurchases: 195000,
    totalPayments: 162500,
    lastPurchaseDate: '17/06/2026',
    status: 'active',
  },
  {
    id: 'b-005',
    name: 'Rajan & Co',
    mobile: '9988776655',
    address: 'Vellore, Tamil Nadu',
    creditLimit: 60000,
    outstanding: 61500,
    totalPurchases: 330000,
    totalPayments: 268500,
    lastPurchaseDate: '15/06/2026',
    status: 'blocked',
  },
  {
    id: 'b-006',
    name: 'Lakshmi Traders',
    mobile: '9123456789',
    address: 'Tambaram, Chennai',
    creditLimit: 80000,
    outstanding: 15000,
    totalPurchases: 620000,
    totalPayments: 605000,
    lastPurchaseDate: '16/06/2026',
    status: 'active',
  },
];

const TRANSACTIONS_BY_BUYER: Record<string, Transaction[]> = {
  'b-001': [
    { id: 't1', date: '18/06/2026', type: 'purchase', description: 'Onion Grade A — Lot #300', amount: 27750, balance: 42500, lotNumber: '300' },
    { id: 't2', date: '17/06/2026', type: 'payment', description: 'UPI Payment', amount: 15000, balance: 14750, method: 'UPI' },
    { id: 't3', date: '16/06/2026', type: 'purchase', description: 'Onion Grade B — Lot #285', amount: 18500, balance: 29750, lotNumber: '285' },
    { id: 't4', date: '14/06/2026', type: 'payment', description: 'Bank Transfer', amount: 20000, balance: 11250, method: 'Bank Transfer' },
    { id: 't5', date: '12/06/2026', type: 'purchase', description: 'Tomato Grade A — Lot #270', amount: 31250, balance: 31250, lotNumber: '270' },
  ],
  'b-003': [
    { id: 't1', date: '18/06/2026', type: 'purchase', description: 'Potato Grade A — Lot #470', amount: 44000, balance: 68000, lotNumber: '470' },
    { id: 't2', date: '15/06/2026', type: 'payment', description: 'Cash Payment', amount: 25000, balance: 24000, method: 'Cash' },
    { id: 't3', date: '13/06/2026', type: 'purchase', description: 'Garlic Grade B — Lot #450', amount: 49000, balance: 49000, lotNumber: '450' },
  ],
};

const ALERT_THRESHOLD = 0.8;

interface BuyerFormData {
  name: string;
  mobile: string;
  address: string;
  creditLimit: string;
}

const EMPTY_FORM: BuyerFormData = { name: '', mobile: '', address: '', creditLimit: '' };

export default function BuyerManagementClient() {
  const [buyers, setBuyers] = useState<Buyer[]>(BUYERS_DATA);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'watch' | 'blocked'>('all');
  const [selectedBuyer, setSelectedBuyer] = useState<Buyer | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editBuyer, setEditBuyer] = useState<Buyer | null>(null);
  const [formData, setFormData] = useState<BuyerFormData>(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState<Partial<BuyerFormData>>({});
  const [expandedHistory, setExpandedHistory] = useState(false);

  const highAlertBuyers = buyers.filter(
    (b) => b.outstanding > 0 && b.outstanding / b.creditLimit >= ALERT_THRESHOLD
  );

  const filtered = buyers.filter((b) => {
    const matchSearch =
      b.name.toLowerCase().includes(search.toLowerCase()) ||
      b.mobile.includes(search);
    const matchStatus = filterStatus === 'all' || b.status === filterStatus;
    return matchSearch && matchStatus;
  });

  function getUtilPct(b: Buyer) {
    return Math.min(Math.round((b.outstanding / b.creditLimit) * 100), 100);
  }

  function getUtilColor(pct: number) {
    if (pct >= 100) return 'bg-danger';
    if (pct >= 80) return 'bg-warning';
    return 'bg-accent';
  }

  function getUtilTextColor(pct: number) {
    if (pct >= 100) return 'text-danger';
    if (pct >= 80) return 'text-warning';
    return 'text-accent';
  }

  function validateForm(data: BuyerFormData) {
    const errors: Partial<BuyerFormData> = {};
    if (!data.name.trim()) errors.name = 'Name is required';
    if (!data.mobile.trim() || !/^\d{10}$/.test(data.mobile)) errors.mobile = 'Valid 10-digit mobile required';
    if (!data.creditLimit || isNaN(Number(data.creditLimit)) || Number(data.creditLimit) <= 0)
      errors.creditLimit = 'Valid credit limit required';
    return errors;
  }

  function handleAddSubmit() {
    const errors = validateForm(formData);
    if (Object.keys(errors).length > 0) { setFormErrors(errors); return; }
    const newBuyer: Buyer = {
      id: `b-${Date.now()}`,
      name: formData.name.trim(),
      mobile: formData.mobile.trim(),
      address: formData.address.trim(),
      creditLimit: Number(formData.creditLimit),
      outstanding: 0,
      totalPurchases: 0,
      totalPayments: 0,
      lastPurchaseDate: '—',
      status: 'active',
    };
    setBuyers((prev) => [newBuyer, ...prev]);
    setShowAddModal(false);
    setFormData(EMPTY_FORM);
    setFormErrors({});
  }

  function handleEditOpen(b: Buyer) {
    setEditBuyer(b);
    setFormData({ name: b.name, mobile: b.mobile, address: b.address, creditLimit: String(b.creditLimit) });
    setFormErrors({});
    setShowEditModal(true);
  }

  function handleEditSubmit() {
    if (!editBuyer) return;
    const errors = validateForm(formData);
    if (Object.keys(errors).length > 0) { setFormErrors(errors); return; }
    setBuyers((prev) =>
      prev.map((b) =>
        b.id === editBuyer.id
          ? { ...b, name: formData.name.trim(), mobile: formData.mobile.trim(), address: formData.address.trim(), creditLimit: Number(formData.creditLimit) }
          : b
      )
    );
    setShowEditModal(false);
    setEditBuyer(null);
    setFormData(EMPTY_FORM);
    setFormErrors({});
  }

  const transactions = selectedBuyer ? (TRANSACTIONS_BY_BUYER[selectedBuyer.id] ?? []) : [];
  const visibleTransactions = expandedHistory ? transactions : transactions.slice(0, 3);

  return (
    <div className="flex flex-col gap-4 p-4 lg:p-6 pb-20 lg:pb-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-foreground">Buyer Management</h1>
          <p className="text-xs text-muted-foreground mt-0.5">{buyers.length} buyers registered</p>
        </div>
        <button
          onClick={() => { setFormData(EMPTY_FORM); setFormErrors({}); setShowAddModal(true); }}
          className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-xl text-sm font-semibold hover:bg-primary/90 transition-colors"
        >
          <Plus size={16} />
          <span className="hidden sm:inline">Add Buyer</span>
        </button>
      </div>

      {/* Auto-Alert Banner */}
      {highAlertBuyers.length > 0 && (
        <div className="bg-danger/5 border border-danger/20 rounded-xl p-3">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle size={16} className="text-danger flex-shrink-0" />
            <p className="text-sm font-semibold text-danger">High Outstanding Alert — {highAlertBuyers.length} buyer{highAlertBuyers.length > 1 ? 's' : ''}</p>
          </div>
          <div className="flex flex-col gap-1.5">
            {highAlertBuyers.map((b) => {
              const pct = getUtilPct(b);
              return (
                <div key={b.id} className="flex items-center gap-3 bg-danger/5 rounded-lg px-3 py-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-foreground truncate">{b.name}</p>
                    <p className="text-2xs text-muted-foreground">
                      ₹{b.outstanding.toLocaleString('en-IN')} of ₹{b.creditLimit.toLocaleString('en-IN')} ({pct}%)
                    </p>
                  </div>
                  <div className="w-20 bg-muted rounded-full h-1.5">
                    <div className={`h-1.5 rounded-full ${getUtilColor(pct)}`} style={{ width: `${pct}%` }} />
                  </div>
                  <span className={`text-xs font-bold ${getUtilTextColor(pct)}`}>{pct}%</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Search + Filter */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search buyer name or mobile..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-card border border-border rounded-xl pl-9 pr-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value as typeof filterStatus)}
          className="bg-card border border-border rounded-xl px-3 py-2.5 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
        >
          <option value="all">All</option>
          <option value="active">Active</option>
          <option value="watch">Watch</option>
          <option value="blocked">Blocked</option>
        </select>
      </div>

      {/* Summary KPIs */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: 'Total Buyers', value: buyers.length, icon: User, color: 'text-info' },
          { label: 'Total Outstanding', value: `₹${buyers.reduce((s, b) => s + b.outstanding, 0).toLocaleString('en-IN')}`, icon: CreditCard, color: 'text-warning' },
          { label: 'High Risk', value: highAlertBuyers.length, icon: AlertTriangle, color: 'text-danger' },
        ].map((kpi) => (
          <div key={kpi.label} className="bg-card border border-border rounded-xl p-3">
            <div className={`${kpi.color} mb-1`}><kpi.icon size={16} /></div>
            <p className="text-base font-bold text-foreground">{kpi.value}</p>
            <p className="text-2xs text-muted-foreground">{kpi.label}</p>
          </div>
        ))}
      </div>

      {/* Buyer List */}
      <div className="flex flex-col gap-2">
        {filtered.length === 0 && (
          <div className="text-center py-12 text-muted-foreground text-sm">No buyers found.</div>
        )}
        {filtered.map((buyer) => {
          const pct = getUtilPct(buyer);
          const isHighAlert = buyer.outstanding > 0 && pct >= 80;
          return (
            <div
              key={buyer.id}
              onClick={() => { setSelectedBuyer(buyer); setExpandedHistory(false); }}
              className={`bg-card border rounded-xl p-4 cursor-pointer hover:border-primary/40 transition-all ${
                isHighAlert ? 'border-warning/40' : 'border-border'
              }`}
            >
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <User size={16} className="text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-foreground">{buyer.name}</p>
                    {isHighAlert && <AlertTriangle size={13} className="text-warning" />}
                    <span className={`text-2xs font-semibold px-2 py-0.5 rounded-full ${
                      buyer.status === 'active' ? 'bg-accent/10 text-accent' :
                      buyer.status === 'watch'? 'bg-warning/10 text-warning' : 'bg-danger/10 text-danger'
                    }`}>
                      {buyer.status.charAt(0).toUpperCase() + buyer.status.slice(1)}
                    </span>
                  </div>
                  <div className="flex items-center gap-1 mt-0.5">
                    <Phone size={11} className="text-muted-foreground" />
                    <p className="text-xs text-muted-foreground">{buyer.mobile}</p>
                  </div>
                  {/* Credit utilization bar */}
                  <div className="mt-2">
                    <div className="flex justify-between text-2xs mb-1">
                      <span className="text-muted-foreground">Outstanding: <span className="text-foreground font-medium">₹{buyer.outstanding.toLocaleString('en-IN')}</span></span>
                      <span className={`font-semibold ${getUtilTextColor(pct)}`}>{pct}% of ₹{buyer.creditLimit.toLocaleString('en-IN')}</span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-1.5">
                      <div className={`h-1.5 rounded-full transition-all ${getUtilColor(pct)}`} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); handleEditOpen(buyer); }}
                  className="p-1.5 rounded-lg hover:bg-muted transition-colors flex-shrink-0"
                >
                  <Edit2 size={14} className="text-muted-foreground" />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Buyer Detail Modal */}
      {selectedBuyer && (
        <Modal
          isOpen={!!selectedBuyer}
          onClose={() => setSelectedBuyer(null)}
          title={selectedBuyer.name}
        >
          <div className="flex flex-col gap-4">
            {/* Alert if high outstanding */}
            {getUtilPct(selectedBuyer) >= 80 && (
              <div className={`flex items-start gap-3 rounded-xl p-3 border ${
                getUtilPct(selectedBuyer) >= 100 ? 'bg-danger/5 border-danger/20' : 'bg-warning/5 border-warning/20'
              }`}>
                <AlertTriangle size={16} className={getUtilPct(selectedBuyer) >= 100 ? 'text-danger' : 'text-warning'} />
                <div>
                  <p className={`text-xs font-semibold ${getUtilPct(selectedBuyer) >= 100 ? 'text-danger' : 'text-warning'}`}>
                    {getUtilPct(selectedBuyer) >= 100 ? 'Credit Limit Exceeded — Do Not Sell' : 'High Outstanding — Caution Before Selling'}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    ₹{selectedBuyer.outstanding.toLocaleString('en-IN')} outstanding of ₹{selectedBuyer.creditLimit.toLocaleString('en-IN')} limit ({getUtilPct(selectedBuyer)}%)
                  </p>
                </div>
              </div>
            )}

            {/* Profile Info */}
            <div className="bg-muted/30 rounded-xl p-3 grid grid-cols-2 gap-3">
              <div>
                <p className="text-2xs text-muted-foreground">Mobile</p>
                <p className="text-sm font-medium text-foreground">{selectedBuyer.mobile}</p>
              </div>
              <div>
                <p className="text-2xs text-muted-foreground">Status</p>
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                  selectedBuyer.status === 'active' ? 'bg-accent/10 text-accent' :
                  selectedBuyer.status === 'watch'? 'bg-warning/10 text-warning' : 'bg-danger/10 text-danger'
                }`}>
                  {selectedBuyer.status.charAt(0).toUpperCase() + selectedBuyer.status.slice(1)}
                </span>
              </div>
              <div className="col-span-2">
                <p className="text-2xs text-muted-foreground">Address</p>
                <p className="text-sm font-medium text-foreground">{selectedBuyer.address || '—'}</p>
              </div>
              <div>
                <p className="text-2xs text-muted-foreground">Last Purchase</p>
                <p className="text-sm font-medium text-foreground">{selectedBuyer.lastPurchaseDate}</p>
              </div>
              <div>
                <p className="text-2xs text-muted-foreground">Credit Limit</p>
                <p className="text-sm font-medium text-foreground">₹{selectedBuyer.creditLimit.toLocaleString('en-IN')}</p>
              </div>
            </div>

            {/* Financial Summary */}
            <div className="grid grid-cols-3 gap-2">
              {[
                { label: 'Outstanding', value: selectedBuyer.outstanding, color: selectedBuyer.outstanding > 0 ? 'text-warning' : 'text-accent', icon: CreditCard },
                { label: 'Total Purchases', value: selectedBuyer.totalPurchases, color: 'text-info', icon: TrendingUp },
                { label: 'Total Payments', value: selectedBuyer.totalPayments, color: 'text-accent', icon: TrendingDown },
              ].map((item) => (
                <div key={item.label} className="bg-card border border-border rounded-xl p-3 text-center">
                  <item.icon size={14} className={`${item.color} mx-auto mb-1`} />
                  <p className={`text-sm font-bold ${item.color}`}>₹{item.value.toLocaleString('en-IN')}</p>
                  <p className="text-2xs text-muted-foreground">{item.label}</p>
                </div>
              ))}
            </div>

            {/* Credit Utilization */}
            <div>
              <div className="flex justify-between text-xs mb-1.5">
                <span className="text-muted-foreground font-medium">Credit Utilization</span>
                <span className={`font-bold ${getUtilTextColor(getUtilPct(selectedBuyer))}`}>{getUtilPct(selectedBuyer)}%</span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div
                  className={`h-2 rounded-full transition-all ${getUtilColor(getUtilPct(selectedBuyer))}`}
                  style={{ width: `${getUtilPct(selectedBuyer)}%` }}
                />
              </div>
            </div>

            {/* Transaction History */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <History size={14} className="text-muted-foreground" />
                <p className="text-sm font-semibold text-foreground">Purchase & Payment History</p>
              </div>
              {transactions.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-4">No transactions yet.</p>
              ) : (
                <div className="flex flex-col gap-1.5">
                  {visibleTransactions.map((tx) => (
                    <div key={tx.id} className="flex items-center gap-3 bg-muted/30 rounded-xl px-3 py-2.5">
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                        tx.type === 'purchase' ? 'bg-info/10' : 'bg-accent/10'
                      }`}>
                        {tx.type === 'purchase'
                          ? <TrendingUp size={13} className="text-info" />
                          : <CheckCircle size={13} className="text-accent" />
                        }
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-foreground truncate">{tx.description}</p>
                        <p className="text-2xs text-muted-foreground">{tx.date}</p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className={`text-xs font-bold ${tx.type === 'purchase' ? 'text-danger' : 'text-accent'}`}>
                          {tx.type === 'purchase' ? '+' : '-'}₹{tx.amount.toLocaleString('en-IN')}
                        </p>
                        <p className="text-2xs text-muted-foreground">Bal: ₹{tx.balance.toLocaleString('en-IN')}</p>
                      </div>
                    </div>
                  ))}
                  {transactions.length > 3 && (
                    <button
                      onClick={() => setExpandedHistory(!expandedHistory)}
                      className="flex items-center justify-center gap-1 text-xs text-primary font-medium py-1.5 hover:underline"
                    >
                      {expandedHistory ? <><ChevronUp size={13} /> Show Less</> : <><ChevronDown size={13} /> Show All {transactions.length} Transactions</>}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </Modal>
      )}

      {/* Add Buyer Modal */}
      <Modal isOpen={showAddModal} onClose={() => setShowAddModal(false)} title="Add New Buyer">
        <BuyerForm
          formData={formData}
          formErrors={formErrors}
          onChange={(field, val) => { setFormData((p) => ({ ...p, [field]: val })); setFormErrors((p) => ({ ...p, [field]: undefined })); }}
          onSubmit={handleAddSubmit}
          onCancel={() => setShowAddModal(false)}
          submitLabel="Add Buyer"
        />
      </Modal>

      {/* Edit Buyer Modal */}
      <Modal isOpen={showEditModal} onClose={() => setShowEditModal(false)} title="Edit Buyer">
        <BuyerForm
          formData={formData}
          formErrors={formErrors}
          onChange={(field, val) => { setFormData((p) => ({ ...p, [field]: val })); setFormErrors((p) => ({ ...p, [field]: undefined })); }}
          onSubmit={handleEditSubmit}
          onCancel={() => setShowEditModal(false)}
          submitLabel="Save Changes"
        />
      </Modal>
    </div>
  );
}

interface BuyerFormProps {
  formData: BuyerFormData;
  formErrors: Partial<BuyerFormData>;
  onChange: (field: keyof BuyerFormData, value: string) => void;
  onSubmit: () => void;
  onCancel: () => void;
  submitLabel: string;
}

function BuyerForm({ formData, formErrors, onChange, onSubmit, onCancel, submitLabel }: BuyerFormProps) {
  return (
    <div className="flex flex-col gap-4">
      {[
        { field: 'name' as const, label: 'Buyer Name', placeholder: 'e.g. Senthil Traders', type: 'text' },
        { field: 'mobile' as const, label: 'Mobile Number', placeholder: '10-digit mobile', type: 'tel' },
        { field: 'address' as const, label: 'Address (optional)', placeholder: 'City / Area', type: 'text' },
        { field: 'creditLimit' as const, label: 'Credit Limit (₹)', placeholder: 'e.g. 50000', type: 'number' },
      ].map(({ field, label, placeholder, type }) => (
        <div key={field}>
          <label className="text-xs font-medium text-muted-foreground mb-1 block">{label}</label>
          <input
            type={type}
            placeholder={placeholder}
            value={formData[field]}
            onChange={(e) => onChange(field, e.target.value)}
            className={`w-full bg-input border rounded-xl px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary ${
              formErrors[field] ? 'border-danger' : 'border-border'
            }`}
          />
          {formErrors[field] && <p className="text-2xs text-danger mt-1">{formErrors[field]}</p>}
        </div>
      ))}
      <div className="flex gap-2 pt-1">
        <button onClick={onCancel} className="flex-1 bg-muted text-foreground py-2.5 rounded-xl text-sm font-semibold hover:bg-muted/80 transition-colors">
          Cancel
        </button>
        <button onClick={onSubmit} className="flex-1 bg-primary text-primary-foreground py-2.5 rounded-xl text-sm font-semibold hover:bg-primary/90 transition-colors">
          {submitLabel}
        </button>
      </div>
    </div>
  );
}
